// Curated catalogue of Azure Neural voices we expose in Settings.
//
// The full list at https://learn.microsoft.com/azure/ai-services/speech-service/language-support
// has hundreds of voices across dozens of locales. For a learner-
// focused app a tight, hand-picked set is more useful than a flat
// dump — picks a few accents per language so the owner can choose
// male vs female, US vs UK, Egyptian vs Saudi Arabic, etc.
//
// All entries are F0-tier eligible (free 500K chars/month). If we
// ever swap a default, the migration is a localStorage key bump,
// not a backend change — the edge function accepts any voice
// string.
//
// Names use the lower-case dialect descriptor first so the picker
// sorts naturally by accent: "British Female (Sonia)".

export const AZURE_VOICES = {
  en: [
    { id: 'en-US-AvaNeural',     label: 'US Female (Ava)',     accent: 'US', gender: 'female', tone: 'conversational' },
    { id: 'en-US-AriaNeural',    label: 'US Female (Aria)',    accent: 'US', gender: 'female', tone: 'news anchor' },
    { id: 'en-US-JennyNeural',   label: 'US Female (Jenny)',   accent: 'US', gender: 'female', tone: 'friendly' },
    { id: 'en-US-EmmaNeural',    label: 'US Female (Emma)',    accent: 'US', gender: 'female', tone: 'warm' },
    { id: 'en-US-AndrewNeural',  label: 'US Male (Andrew)',    accent: 'US', gender: 'male',   tone: 'conversational' },
    { id: 'en-US-BrianNeural',   label: 'US Male (Brian)',     accent: 'US', gender: 'male',   tone: 'deep' },
    { id: 'en-US-GuyNeural',     label: 'US Male (Guy)',       accent: 'US', gender: 'male',   tone: 'professional' },
    { id: 'en-GB-SoniaNeural',   label: 'British Female (Sonia)', accent: 'UK', gender: 'female', tone: 'warm' },
    { id: 'en-GB-RyanNeural',    label: 'British Male (Ryan)', accent: 'UK', gender: 'male',   tone: 'conversational' },
    { id: 'en-GB-LibbyNeural',   label: 'British Female (Libby)', accent: 'UK', gender: 'female', tone: 'friendly' },
    { id: 'en-AU-NatashaNeural', label: 'Australian Female (Natasha)', accent: 'AU', gender: 'female', tone: 'warm' },
    { id: 'en-AU-WilliamNeural', label: 'Australian Male (William)',   accent: 'AU', gender: 'male',   tone: 'conversational' },
  ],
  ar: [
    { id: 'ar-EG-SalmaNeural',   label: 'Egyptian Female (Salma)',  accent: 'EG', gender: 'female', tone: 'warm' },
    { id: 'ar-EG-ShakirNeural',  label: 'Egyptian Male (Shakir)',   accent: 'EG', gender: 'male',   tone: 'conversational' },
    { id: 'ar-SA-ZariyahNeural', label: 'Saudi Female (Zariyah)',   accent: 'SA', gender: 'female', tone: 'conversational' },
    { id: 'ar-SA-HamedNeural',   label: 'Saudi Male (Hamed)',       accent: 'SA', gender: 'male',   tone: 'professional' },
    { id: 'ar-AE-FatimaNeural',  label: 'Emirati Female (Fatima)',  accent: 'AE', gender: 'female', tone: 'conversational' },
    { id: 'ar-MA-MounaNeural',   label: 'Moroccan Female (Mouna)',  accent: 'MA', gender: 'female', tone: 'conversational' },
  ],
}

export const DEFAULT_VOICE = {
  en: 'en-US-BrianNeural',
  ar: 'ar-EG-ShakirNeural',
}

// localStorage keys per language so the owner can pick a different
// voice for English vs Arabic independently.
const STORAGE_KEY = (lang) => `phoenix:tts-voice-${lang}`

export function readVoicePref(lang) {
  if (typeof window === 'undefined') return DEFAULT_VOICE[lang] || null
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY(lang))
    if (stored && AZURE_VOICES[lang]?.some((v) => v.id === stored)) return stored
  } catch { /* private mode */ }
  return DEFAULT_VOICE[lang] || null
}

export function writeVoicePref(lang, voiceId) {
  if (typeof window === 'undefined') return
  try {
    if (voiceId) window.localStorage.setItem(STORAGE_KEY(lang), voiceId)
    else window.localStorage.removeItem(STORAGE_KEY(lang))
  } catch { /* private mode */ }
}
